// shellHistory: how the shell rides the browser's history.
//
// The dashboard is one document, so Back would leave the app. The shell tucks
// its own location (`osNav`) into history.state beside Next's markers and moves
// it on `popstate`. Two rules live here, pure, so they can be tested:
//
//   · which kind of step a change of location is (push a new entry, replace
//     the current one, or nothing);
//   · what to stamp back onto an entry that lost its `osNav`. Next rewrites
//     history.state on router.refresh() and on soft navigation WITHOUT the keys
//     it does not know, so after any refresh the entry the person is standing
//     on stopped being one the shell could return to: Back did nothing, and the
//     next Back left the app.

export interface OsNav {
  path: string;
  viewerIndex: number | null;
}

/** a step through the shell's own location. */
export type HistoryStep = "push" | "replace" | "none";

/**
 * Moving from `prev` to `next`.
 *
 * Paging from one document to another inside the SAME reader replaces: each
 * swipe is a turn of a page, not a place to come back to, so it must not cost a
 * Back press. Opening a reader (index from nothing) and closing one still push,
 * so Back from an open document closes it.
 */
export function historyStep(prev: OsNav, next: OsNav): HistoryStep {
  if (prev.path === next.path && prev.viewerIndex === next.viewerIndex) return "none";
  if (prev.path === next.path && prev.viewerIndex != null && next.viewerIndex != null) return "replace";
  return "push";
}

/** the shell's location stored on a history entry, or null if it has none. */
export function osNavOf(state: unknown): OsNav | null {
  const nav = (state as { osNav?: { path?: unknown; viewerIndex?: unknown } } | null)?.osNav;
  if (!nav || typeof nav.path !== "string") return null;
  return { path: nav.path, viewerIndex: typeof nav.viewerIndex === "number" ? nav.viewerIndex : null };
}

/** does this entry already carry exactly `nav`? */
export function sameNav(state: unknown, nav: OsNav): boolean {
  const have = osNavOf(state);
  return !!have && have.path === nav.path && have.viewerIndex === nav.viewerIndex;
}

/** `state` with the shell's location put back, keeping every other key (Next's
 *  `__NA` and tree above all: dropping them turns Back into a full reload). */
export function withOsNav(state: unknown, nav: OsNav): Record<string, unknown> {
  const base = state && typeof state === "object" ? (state as Record<string, unknown>) : {};
  return { ...base, osNav: { path: nav.path, viewerIndex: nav.viewerIndex } };
}
